import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const STORAGE_KEY = "cynos_recent_purchase_toast_ts";
const ONE_HOUR_MS = 60 * 60 * 1000; // 1 hour (3,600,000 ms)
const DISPLAY_DURATION_MS = 5000; // Visible for 5 seconds
const INITIAL_DELAY_MS = 15000; // Wait 15s after landing so visitor isn't immediately interrupted

const NAMES = [
  "Rahul from Delhi",
  "Priya from Mumbai",
  "Aarav from Bengaluru",
  "Sneha from Pune",
  "Karan from Hyderabad",
  "Ananya from Kolkata",
  "Vikram from Jaipur",
  "Meera from Chennai",
];

const PRODUCTS = [
  "Onyx Oversized Tee",
  "Shadow Anime Tee",
  "Renegade Graphic Tee",
  "Couple Tee Set",
  "Midnight Essential Tee",
  "Eclipse Crop Tee",
];

const RecentPurchaseToast = () => {
  const [visible, setVisible] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const hideTimerRef = useRef(null);
  const showTimerRef = useRef(null);

  const dismissToast = () => {
    setVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch {
      // Ignore localStorage exceptions in restricted/private browsing modes
    }
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
  };

  useEffect(() => {
    const scheduleNextToast = () => {
      let lastShown = 0;
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          lastShown = parseInt(stored, 10) || 0;
        }
      } catch {
        lastShown = 0;
      }

      const elapsed = Date.now() - lastShown;
      let delay = INITIAL_DELAY_MS;

      if (lastShown > 0 && elapsed < ONE_HOUR_MS) {
        // Less than 1 hour elapsed, wait for remainder of the hour
        delay = ONE_HOUR_MS - elapsed;
      }

      showTimerRef.current = setTimeout(() => {
        const randomIdx = Math.floor(Math.random() * NAMES.length);
        setCurrentIdx(randomIdx);
        setVisible(true);

        try {
          localStorage.setItem(STORAGE_KEY, Date.now().toString());
        } catch {}

        hideTimerRef.current = setTimeout(() => {
          setVisible(false);
          scheduleNextToast();
        }, DISPLAY_DURATION_MS);
      }, delay);
    };

    scheduleNextToast();

    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const name = NAMES[currentIdx % NAMES.length];
  const product = PRODUCTS[currentIdx % PRODUCTS.length];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 30, x: -10 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.4 }}
          className="fixed bottom-6 left-4 z-30 bg-cynos-card border border-white/10 max-w-xs hidden md:flex items-center gap-3 p-3 pr-10"
          data-testid="recent-purchase-toast"
        >
          <div className="w-12 h-12 bg-cynos-surface flex items-center justify-center font-anton text-2xl text-cynos-red">
            {name[0]}
          </div>
          <div className="flex-1">
            <div className="text-xs text-white">
              <span className="font-bold">{name}</span> just bought
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-0.5 truncate">
              {product}
            </div>
          </div>
          <button
            onClick={dismissToast}
            aria-label="Dismiss notification"
            className="absolute top-2 right-2 text-zinc-500 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RecentPurchaseToast;
